<template>
  <div class="flex flex-col rounded p-3">
    <!-- Welcome Header -->
    <div class="mb-8">
      <h1 class="text-4xl font-bold text-gray-800">
        Welcome back, <span class="text-blue">{{ userProfile?.givenName }}</span>! 👋
      </h1>
      <p class="text-gray-600 mt-2">
        {{ getGreeting() }} - Here's your dashboard overview
      </p>
    </div>

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- User Profile Card -->
      <div class="lg:col-span-2 bg-white rounded-xl shadow-lg overflow-hidden">
        <div class="bg-gradient-to-r from-primary to-primary-700 p-3">
          <h2 class="text-lg font-bold text-white">Profile Information</h2>
        </div>

        <div class="p-6">
          <div class="flex items-start gap-6">
            <!-- Avatar -->
            <div class="flex-shrink-0">
              <div class="w-24 h-24 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white shadow-lg">
                <i class="fa fa-user text-4xl"></i>
              </div>
            </div>

            <!-- User Details -->
            <div class="flex-1">
              <h3 class="text-2xl font-bold text-gray-800 mb-2">
                {{ userProfile?.givenName }} {{ userProfile?.surName }}
              </h3>

              <div class="space-y-2 text-sm text-gray-600">
                <div class="flex items-center gap-2">
                  <i class="fa fa-envelope text-blue w-4"></i>
                  <a :href="`mailto:${userProfile?.email}`" class="hover:text-blue transition-colors">
                    {{ userProfile?.email }}
                  </a>
                </div>

                <div class="flex items-center gap-2">
                  <i class="fa fa-phone text-blue w-4"></i>
                  <span>{{ userProfile?.phoneNumber }}</span>
                </div>

                <div class="flex items-center gap-2">
                  <i class="fa fa-id-card text-blue w-4"></i>
                  <span>
                    {{ formatIdType(userProfile?.idType) }}: {{ userProfile?.idNumber }}
                  </span>
                </div>

                <div class="flex items-center gap-2">
                  <i class="fa fa-building text-blue w-4"></i>
                  <span>{{ userProfile?.department?.departmentName }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Stats Card -->
      <div class="bg-white rounded-xl shadow-lg overflow-hidden">
        <div class="bg-gradient-to-r from-green to-green-800 p-3">
          <h2 class="text-lg font-bold text-white">Quick Stats</h2>
        </div>

        <div class="p-6 space-y-4">
          <div class="flex items-center justify-between p-4 bg-primary-50 rounded-lg">
            <div>
              <p class="text-sm text-gray-600">Total Roles</p>
              <p class="text-2xl font-bold text-blue">{{ userProfile?.roles?.length || 0 }}</p>
            </div>
          </div>

          <div class="flex items-center justify-between p-4 bg-green-50 rounded-lg">
            <div>
              <p class="text-sm text-gray-600">Total Permissions</p>
              <p class="text-2xl font-bold text-green">{{ totalPermissions }}</p>
            </div>
          </div>

          <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p class="text-sm text-gray-600">Account Status</p>
              <p class="text-lg font-semibold text-green">
                <i class="fa fa-check-circle mr-1"></i> Active
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- User Roles Section -->
    <div class="mt-6 bg-white rounded-xl shadow-lg overflow-hidden">
      <div class="bg-gradient-to-r from-primary-600 to-primary-700 p-3">
        <h2 class="text-lg font-bold text-white">Your Roles & Permissions</h2>
        <p class="text-primary-100 text-xs mt-1">View all assigned roles and their permission counts</p>
      </div>

      <div class="p-6">
        <div v-if="userProfile?.roles && userProfile.roles.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="(role, idx) in userProfile.roles"
            :key="idx"
            class="border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-blue transition-all duration-200">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <h3 class="font-semibold text-gray-800 text-lg">
                  {{ formatRoleName(role.name) }}
                </h3>
                <div class="flex items-center gap-2 text-sm text-gray-600">
                  <i class="fa fa-key text-blue"></i>
                  <span>{{ role.permissions?.length || 0 }} Permissions</span>
                </div>
              </div>
              <div class="shrink-0">
                <div class="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <i class="fa fa-gear text-blue text-xl"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-12 text-gray-500">
          <i class="fa fa-user-shield text-5xl text-gray-300 mb-4"></i>
          <p>No roles assigned yet</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'

// Types
interface Department {
  departmentName: string
  [key: string]: any
}

interface Role {
  name: string
  permissions: any[]
  [key: string]: any
}

interface UserProfile {
  givenName?: string
  surName?: string
  email?: string
  phoneNumber?: string
  idType?: string
  idNumber?: string
  department?: Department
  roles?: Role[]
  [key: string]: any
}

// Reactive state
const userProfile = ref<UserProfile | null>(null)

// Computed
const totalPermissions = computed(() => {
  if (!userProfile.value?.roles) return 0
  return userProfile.value.roles.reduce((total, role) => total + (role.permissions?.length || 0), 0)
})

// Methods
const getGreeting = (): string => {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

const formatIdType = (idType?: string): string => {
  if (!idType) return ''
  return idType.replaceAll('_', ' ').toUpperCase()
}

const formatRoleName = (roleName: string): string => {
  if (!roleName) return ''
  return roleName
    .replaceAll('_', ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Lifecycle
onMounted(() => {
  const storedUser = sessionStorage.getItem("current_user")
  if (storedUser) {
    try {
      userProfile.value = JSON.parse(storedUser)
    } catch (error) {
      console.error('Failed to parse user profile:', error)
    }
  }
})
</script>
